import { IssueModel } from 'shared/domain/issue/issueModel';
import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';
import {
  AttachmentsScope,
  ImageDisplayStyle,
} from '../../types/model/common';
import {
  ReportPageEstimateInput
} from '../buildReportPageEstimateInput';
import { hasIssueFieldValueForPdfEstimate } from '../hasIssueFieldValueForPdfEstimate';
import {
  createIsIssueFieldVisible,
  isPdfSectionAvailable,
  PDF_ESTIMATE_SECTIONS,
  PDF_KEY_INFO_BASE_FIELD_COUNT,
  PDF_SHARED_SECTION_ORDER,
  PDF_KEY_INFO_FIXED_ADDITIONAL_FIELD_COUNT,
  PdfEstimateSectionKey,
} from '../pdfFieldSections';
import {
  ReportIssueEstimateRow,
} from '../reportIssueEstimateRow';
import {
  estimatePlainTextHeight,
  singleLineHeight,
} from './measurePdfText';
import {
  CONTENT_WIDTH,
  FONT_SIZE,
} from './pdfLayoutConstants';
import { documentSectionNodes, SECTION_HEADER_HEIGHT } from './documentSectionNodes';
import { messageSectionNodes } from './messageSectionNodes';
import {
  LayoutNode,
  leaf,
  singlePage,
} from './pdfLayoutEngine';

/**
 * Calibrated heights (PDF points) of the recurring layout blocks. Derived from the PDF
 * microservice font sizes and validated against reference reports — see
 * `pdfLayoutConstants` for the geometry these build on.
 */
const REPORT_TITLE_HEIGHT = 28;
/** A `textWithLabel` with a single-line value: label (8pt) + value (9pt) + spacing. */
const TEXT_WITH_LABEL_HEIGHT =
  singleLineHeight(FONT_SIZE.label) + singleLineHeight(FONT_SIZE.content);
/** One grid row of single-line fields. */
const FIELD_ROW_HEIGHT = 32;
/**
 * Share of a grid row a field takes. The backend sizes a field by its longest text
 * (`CalculateColumnX`): a filled one is calibrated at half the row, an empty one prints
 * a dash under its label and is narrow, so several of them share a row.
 */
const FILLED_FIELD_WIDTH = 1 / 2;
const EMPTY_FIELD_WIDTH = 1 / 4;
/** Tolerance for summing the width shares (floating point). */
const ROW_WIDTH_EPSILON = 1e-9;
const MAP_ROW_HEIGHT = 270;
const MAIN_IMAGE_MINIATURE_HEIGHT = 185;
const APPROVED_BY_HEIGHT = 130;

const HALF_FIELD_WIDTH = (CONTENT_WIDTH - 16) / 2;

/** Rows a grid of fields fills: fields packed left to right, a new row when the next one does not fit. */
function gridRowsForFieldWidths(widths: number[]): number {
  let rows = 0;
  let usedWidth = 1;
  for (const width of widths) {
    if (usedWidth + width > 1 + ROW_WIDTH_EPSILON) {
      rows += 1;
      usedWidth = width;
    } else {
      usedWidth += width;
    }
  }
  return rows;
}

/** What the estimate knows of a row's fields: which are printed and how wide. */
type RowFields = {
  isVisible: (fieldName: IssueFieldNames) => boolean;
  width: (fieldName: IssueFieldNames) => number;
};

/**
 * The fields of a row's issue as the PDF prints them. Whether the issue has
 * attachments comes from the row's counted documents: the issue model of the
 * worker carries counts, not the documents.
 */
function createRowFields(row: ReportIssueEstimateRow, input: ReportPageEstimateInput): RowFields {
  const { issue } = row;
  const valuesKnownFromRow: Partial<Record<IssueFieldNames, boolean>> = {
    [IssueFieldNames.documents]: row.primaryDocuments.all > 0,
  };
  const hasValue = (fieldName: IssueFieldNames): boolean =>
    valuesKnownFromRow[fieldName] ?? hasIssueFieldValueForPdfEstimate(issue, fieldName);
  return {
    isVisible: createIsIssueFieldVisible(issue, input, hasValue),
    width: (fieldName) => (hasValue(fieldName) ? FILLED_FIELD_WIDTH : EMPTY_FIELD_WIDTH),
  };
}

function fieldRowNodes(rowCount: number): LayoutNode[] {
  return Array.from({ length: rowCount }, () => leaf(FIELD_ROW_HEIGHT));
}

/** Extra wrapped lines (beyond the first) for the full-width description field. */
function descriptionExtraHeight(
  issue: IssueModel,
  isFieldAvailable: (fieldName: IssueFieldNames) => boolean
): number {
  if (!isFieldAvailable(IssueFieldNames.description)) {
    return 0;
  }
  const text = issue.primaryData.description ?? '';
  const height = estimatePlainTextHeight(
    text,
    FONT_SIZE.content,
    CONTENT_WIDTH
  );
  const oneLine = singleLineHeight(FONT_SIZE.content);
  return Math.max(0, height - oneLine);
}

/** The key information grid: the fields always printed, then the optional ones of the base section. */
function keyInfoFieldRows({ isVisible, width }: RowFields): number {
  const alwaysPrinted = Array<number>(PDF_KEY_INFO_BASE_FIELD_COUNT + PDF_KEY_INFO_FIXED_ADDITIONAL_FIELD_COUNT).fill(
    FILLED_FIELD_WIDTH
  );
  const optional = PDF_ESTIMATE_SECTIONS[PdfEstimateSectionKey.baseInfo].filter(isVisible).map(width);
  return gridRowsForFieldWidths([...alwaysPrinted, ...optional]);
}

function buildFieldNodes(row: ReportIssueEstimateRow, input: ReportPageEstimateInput, fields: RowFields): LayoutNode[] {
  const { issue } = row;
  const nodes: LayoutNode[] = [];

  if (fields.isVisible(IssueFieldNames.positionOnMap)) {
    nodes.push(leaf(MAP_ROW_HEIGHT));
  } else if (
    issue.mainImage &&
    input.issueAttachments.displayStyle === ImageDisplayStyle.miniatures
  ) {
    nodes.push(leaf(MAIN_IMAGE_MINIATURE_HEIGHT));
  }

  // Key information grid.
  nodes.push(leaf(SECTION_HEADER_HEIGHT), ...fieldRowNodes(keyInfoFieldRows(fields)));
  const descExtra = descriptionExtraHeight(issue, fields.isVisible);
  if (descExtra > 0) {
    nodes.push(leaf(descExtra));
  }

  // Shared sections, each its own header + grid.
  for (const sectionKey of PDF_SHARED_SECTION_ORDER) {
    const widths = PDF_ESTIMATE_SECTIONS[sectionKey].filter(fields.isVisible).map(fields.width);
    if (widths.length === 0) {
      continue;
    }
    nodes.push(leaf(SECTION_HEADER_HEIGHT), ...fieldRowNodes(gridRowsForFieldWidths(widths)));
  }

  return nodes;
}

export function approvedByNode(): LayoutNode {
  return singlePage([leaf(APPROVED_BY_HEIGHT)]);
}

/** Top block of a single-issue report (title, optional report comment, author). */
function singleIssueHeaderNodes(
  issue: IssueModel,
  input: ReportPageEstimateInput
): LayoutNode[] {
  const nodes: LayoutNode[] = [leaf(REPORT_TITLE_HEIGHT)];

  if (input.reportMessageInPdf && input.reportMessageChars > 0) {
    const messageHeight =
      singleLineHeight(FONT_SIZE.label) +
      estimatePlainTextHeight(
        'x'.repeat(input.reportMessageChars),
        FONT_SIZE.content,
        CONTENT_WIDTH
      );
    nodes.push(leaf(messageHeight));
  }

  nodes.push(leaf(TEXT_WITH_LABEL_HEIGHT));
  return nodes;
}

/**
 * Builds the layout nodes for a single issue body: fields, attachments, linked
 * documentation and messages (in the order the backend factory emits them).
 */
export function buildIssueBodyNodes(
  row: ReportIssueEstimateRow,
  input: ReportPageEstimateInput
): LayoutNode[] {
  const { issue } = row;
  const fields = createRowFields(row, input);
  const nodes: LayoutNode[] = [...buildFieldNodes(row, input, fields)];

  if (input.issueAttachments.enabled && isPdfSectionAvailable(PdfEstimateSectionKey.attachments, fields.isVisible)) {
    nodes.push(
      ...documentSectionNodes({
        counts: row.primaryDocuments,
        displayStyle: input.issueAttachments.displayStyle,
        scope: input.issueAttachments.scope,
        hasMainImage: Boolean(issue.mainImage),
      })
    );
  }

  if (
    input.linkedDocumentation.enabled &&
    isPdfSectionAvailable(PdfEstimateSectionKey.linkedDocumentation, fields.isVisible)
  ) {
    nodes.push(
      ...documentSectionNodes({
        counts: row.linkedDocuments,
        displayStyle: input.linkedDocumentation.displayStyle,
        scope: AttachmentsScope.all,
        hasMainImage: false,
      })
    );
  }

  nodes.push(...messageSectionNodes(row.events, input.events));
  nodes.push(
    ...messageSectionNodes(row.emails, input.incomingEmailMessages)
  );
  nodes.push(
    ...messageSectionNodes(row.decisions, input.workflowDecisions)
  );

  return nodes;
}

export function buildSingleIssueNodes(
  row: ReportIssueEstimateRow,
  input: ReportPageEstimateInput
): LayoutNode[] {
  return [
    ...singleIssueHeaderNodes(row.issue, input),
    ...buildIssueBodyNodes(row, input),
    approvedByNode(),
  ];
}

export {
  FIELD_ROW_HEIGHT, REPORT_TITLE_HEIGHT, SECTION_HEADER_HEIGHT,
};

